import { useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { MessageBar, MessageBarBody, Spinner } from '@fluentui/react-components';
import { isComplianceTracked, type OfferRecord } from '@vigeo/shared';
import { OfferRecordFrame } from '@/features/offers';
import { SendOffer } from '@/features/offer-stage';
import { fetchHrWorkflow } from '@/features/hr-workflow';
import { fetchCatalogOptions, fetchChecklist, fetchPerson } from '@/lib/api';
import { fetchAuthState } from '@/lib/auth';

/**
 * Row 1.4's own route, kept so the Offers list and old links still land somewhere. The screen is
 * `SendOffer`, which Offer and stage draws inline too (#440); this adds only the record frame.
 */
export function OfferSendPage() {
  const { id = '' } = useParams();
  const [search] = useSearchParams();
  const recordId = search.get('record') ?? '';

  const auth = useQuery({ queryKey: ['auth'], queryFn: fetchAuthState, retry: false });
  const person = useQuery({ queryKey: ['person', id], queryFn: () => fetchPerson(id), enabled: id !== '' });
  // Not asked for staff added by hand: they are not tracked yet (ADR-016 §4), so there is nothing to derive.
  const checklist = useQuery({ queryKey: ['checklist', id], queryFn: () => fetchChecklist(id), enabled: id !== '' && person.data !== undefined && isComplianceTracked(person.data) });
  const options = useQuery({ queryKey: ['catalog-options'], queryFn: fetchCatalogOptions });
  const workflow = useQuery({ queryKey: ['hr-workflow', id], queryFn: () => fetchHrWorkflow(id), enabled: id !== '' });

  const canEdit = auth.data?.status === 'signed-in' && auth.data.user.isAdmin;
  const record = workflow.data?.records.find((r) => r.id === recordId && r.kind === 'offer') as OfferRecord | undefined;

  if (person.isPending || workflow.isPending) return <div><Spinner size="small" label="Loading this offer" /></div>;
  if (person.isError || person.data === undefined) {
    return <MessageBar intent="error"><MessageBarBody>No such person, or you do not have access to them.</MessageBarBody></MessageBar>;
  }
  if (record === undefined || workflow.data === undefined) {
    return <MessageBar intent="error"><MessageBarBody>No such offer. It may have been withdrawn, or replaced by a new draft.</MessageBarBody></MessageBar>;
  }

  const roles = person.data.jobProfileKeys.map((key) => options.data?.jobProfiles.find((p) => p.key === key)?.label ?? 'Role');
  return (
    <OfferRecordFrame person={person.data} readiness={checklist.data?.state ?? null} roles={roles} currentStep={1} canEdit={canEdit}>
      <SendOffer person={person.data} record={record} workflow={workflow.data} canEdit={canEdit} />
    </OfferRecordFrame>
  );
}
